import { useCallback, useMemo, useRef, useState } from "react";
import { ProctoringResultViewProps } from "../ProctoringResultView";
import substitutePhoto from "../../../../images/profile_substitute.svg";
import { deleteRecording as deleteSessionRecording } from "./proctoringResultViewHelper";

type UseResultComponentsProps = Pick<
	ProctoringResultViewProps,
	"setShowStudentResults" | "setSelectedStudent" | "studentList" | "selectedStudent" | "setProctoringSessions"
>;

export default function useResultComponents({
	setShowStudentResults,
	setSelectedStudent,
	studentList,
	setProctoringSessions,
	selectedStudent,
}: UseResultComponentsProps) {
	const [emailModalShowing, setEmailModalShowing] = useState<boolean>(false);
	const [isDeleteConfirmationModalShown, setIsDeleteConfirmationModalShown] = useState<
		"recording" | "roomscan" | undefined
	>();

	const [mediaGridHeight, setMediaGridHeight] = useState(0);

	const showBackButton = useMemo(() => Boolean(setShowStudentResults), [setShowStudentResults]);

	// "Back" is a single intent: this view is done, close it. What closing means — unmounting, playing a
	// drawer transition, navigating away, clearing the host's selection — is the host's business, so this
	// must not also clear selectedStudent. Hosts that derive the session list from the selected student
	// would otherwise render this view once with no session before it goes away, firing a round of
	// session-scoped requests against an undefined session uuid.
	const handleBackButton = useCallback(() => {
		setShowStudentResults?.(false);
	}, [setShowStudentResults]);

	const toggleEmailModal = useCallback((showing: boolean) => {
		setEmailModalShowing(showing);
	}, []);

	const toggleDeleteConfirmationModal = useCallback((videoType: "recording" | "roomscan" | undefined) => {
		setIsDeleteConfirmationModalShown(videoType);
	}, []);

	const handleImageError = useCallback((event: React.SyntheticEvent<HTMLImageElement, Event>) => {
		if (event.target instanceof HTMLImageElement && event.target.src !== substitutePhoto) {
			event.target.src = substitutePhoto;
		}
	}, []);

	const handleStudentChange = useCallback(
		(studentId?: number, dir?: "next" | "prev") => {
			if (dir === "next" || dir === "prev") {
				const currentIndex = studentList.findIndex((student) => student.id === selectedStudent);
				const numStudents = studentList.length;
				const nextIndex =
					dir === "next" ? (currentIndex + 1) % numStudents : (currentIndex - 1 + numStudents) % numStudents;
				const nextStudentId = studentList[nextIndex].id;
				setSelectedStudent(nextStudentId);
				return;
			}

			setSelectedStudent(studentId);
		},
		[selectedStudent, setSelectedStudent, studentList],
	);

	const handleSelectPreviousStudent = useCallback(() => {
		handleStudentChange(undefined, "prev");
	}, [handleStudentChange]);

	const handleSelectNextStudent = useCallback(() => {
		handleStudentChange(undefined, "next");
	}, [handleStudentChange]);

	const handleSelectStudent = useCallback(
		(studentId?: number) => {
			handleStudentChange(studentId);
		},
		[handleStudentChange],
	);

	const deleteRecording = useCallback(
		async (sessionUuid: string) => {
			const isRecording = isDeleteConfirmationModalShown === "recording";
			await deleteSessionRecording(sessionUuid, isRecording ? "recording" : "roomScan");

			// update the appropriate deleted flag in props.proctoringSessions without rerendering the parent component.
			setProctoringSessions?.((prevSessions) => {
				if (prevSessions) {
					const sessionIndex = prevSessions.findIndex((session) => session.uuid === sessionUuid);
					if (sessionIndex !== -1) {
						const session = prevSessions[sessionIndex];
						if (isRecording) {
							session.isWebcamAndScreenCaptureDeleted = true;
						} else {
							session.isRoomScanDeleted = true;
						}
					}
				}
				return prevSessions;
			});
			return isDeleteConfirmationModalShown;
		},
		[isDeleteConfirmationModalShown, setProctoringSessions],
	);

	const mediaGridObserverRef = useRef<ResizeObserver | null>(null);

	// Callback ref instead of a mount-only effect + getElementById: the main tab panel node is
	// remounted (new DOM element) whenever the tab changes because it's keyed by mainTab, so an
	// observer attached once on mount would keep watching a detached node after switching tabs.
	const mediaGridRef = useCallback((node: HTMLDivElement | null) => {
		mediaGridObserverRef.current?.disconnect();
		mediaGridObserverRef.current = null;

		if (!node) {
			return;
		}

		const observer = new ResizeObserver((entries) => {
			for (const entry of entries) {
				if (entry.target === node) {
					// + 120 to avoid extra space below TimestampLinks event table.
					setMediaGridHeight(entry.contentRect.height + 120);
				}
			}
		});

		observer.observe(node);
		mediaGridObserverRef.current = observer;
	}, []);

	return {
		back: {
			showBackButton,
			handleBackButton,
		},
		modal: {
			emailModalShowing,
			toggleEmailModal,
			isDeleteConfirmationModalShown,
			toggleDeleteConfirmationModal,
			deleteRecording,
		},
		image: {
			handleImageError,
		},
		selectStudent: {
			handleSelectPreviousStudent,
			handleSelectNextStudent,
			handleSelectStudent,
		},
		mediaGrid: {
			mediaGridHeight,
			mediaGridRef,
		},
	};
}
